import {useQuery} from "@tanstack/react-query"
import {studentService} from "services"
import {BaseDepartmentId} from "types/departments"
import {useMemo} from "react"
import {checkPermission} from "helpers"
import {Permissions, PermissionUserType} from "types/permission"
import {Model} from "Model"
import {AppliedFilter, FilterList, formatDateFilterValue, resolveYesNoFilterValue} from "types/filter"

const queryCacheKey = "department-student"

type DepartmentStudentProps = {
    studentId?: number
    departmentId?: BaseDepartmentId | number
    isEnabled?: boolean
}

export const useDepartmentStudent = ({studentId, departmentId, isEnabled = true}: DepartmentStudentProps) => {
    const query = useQuery(
        [queryCacheKey, studentId, departmentId],
        async () => {
            const {
                data: [student]
            } = await studentService.getDepartmentStudents({
                skipTotal: true,
                filter: {
                    departmentId,
                    profileIds: [studentId]
                }
            })
            return student
        },
        {
            enabled: !!studentId && isEnabled
        }
    )

    return {
        student: query.data,
        isLoading: query.isFetching
    }
}

export const useCanShowAllStudents = (model: Model, departmentId: number) =>
    useMemo(() => {
        let permissions: PermissionUserType = {}
        switch (departmentId) {
            case BaseDepartmentId.FinancialAid:
                permissions = {staff: [Permissions.Staff.FinancialAid.Director.View]}
                break
            case BaseDepartmentId.StudentAccount:
                permissions = {staff: [Permissions.Staff.StudentAccount.Director.View]}
                break
            case BaseDepartmentId.Academics:
            case BaseDepartmentId.AcademicAffair:
            case BaseDepartmentId.StudentServices:
                permissions = {staff: [Permissions.Staff.StudentServicesAndAcademicAffairs.Director.View]}
                break
            case BaseDepartmentId.Admissions:
            default:
                permissions = {staff: [Permissions.Staff.Admissions.Director.View]}
        }
        return checkPermission(permissions, model)
    }, [model, departmentId])

export const useCanHaveAccessOthers = (model: Model, departmentId: number) =>
    useMemo(() => {
        let permissions: PermissionUserType = {}
        switch (departmentId) {
            case BaseDepartmentId.FinancialAid:
            case BaseDepartmentId.StudentAccount:
                permissions = {staff: [Permissions.Staff.FinancialAid.Students.PermissionsToAccessOthers.View]}
                break
            case BaseDepartmentId.Academics:
            case BaseDepartmentId.AcademicAffair:
            case BaseDepartmentId.StudentServices:
                permissions = {
                    staff: [Permissions.Staff.Academics.Instructional.Students.PermissionsToAccessOthers.View]
                }
                break
            case BaseDepartmentId.Admissions:
            default:
                permissions = {staff: [Permissions.Staff.Admissions.Students.PermissionsToAccessOthers.View]}
        }
        return checkPermission(permissions, model)
    }, [model, departmentId])

export const useStudentListV2Filters = (filters: AppliedFilter["filters"]) =>
    useMemo(() => {
        const filter: FilterList = {}

        const {
            profiles,
            campus,
            program,
            acadAdvisors,
            faAdvisors,
            admAdvisors,
            term,
            startingTerm,
            currentTerm,
            checklist,
            status,
            state,
            profileStatus,
            applicationCompletedDate,
            startDateRange,
            createdAtRange,
            inquiryDateRange,
            lastActivityDateRange,
            includeArchive,
            showDueOnly,
            fullyEnrolled,
            onlySEV,
            specialEvent,
            sapCheckpoints,
            retentionLevels,
            studentStatusIds,
            admissionsLeadSourceIds,
            leadTypeIds,
            isAccessDisabled,
            phases,
            shifts,
            activityIds,
            activityDescription,
            temporaryOutDateRange,
            withdrawalDateRange,
            users,
            countries,
            attendancePercentage
        } = filters

        if (fullyEnrolled) filter.fullyEnrolled = fullyEnrolled
        if (includeArchive) filter.includeArchive = includeArchive
        if (showDueOnly) filter.showDueOnly = showDueOnly
        if (onlySEV) filter.onlySEV = onlySEV
        if (specialEvent) {
            filter.specialEvent = {
                ...specialEvent,
                value: resolveYesNoFilterValue(specialEvent.value)
            }
        }
        if (checklist) {
            filter.checklistCompleted = {
                operator: checklist.operator,
                value: checklist.value.id
            }
        }

        if (profiles) {
            filter.profileIds = {
                operator: profiles.operator,
                value: profiles.value.map(({profileId}) => profileId)
            }
        }
        if (campus) {
            filter.campusIds = {
                operator: campus.operator,
                value: campus.value.map(({id}) => id)
            }
        }
        if (program) {
            filter.programIds = {
                operator: program.operator,
                value: program.value.map(({id}) => id)
            }
        }
        if (studentStatusIds) {
            filter.studentStatusIds = {
                operator: studentStatusIds.operator,
                value: studentStatusIds.value.map(({statusId}) => statusId)
            }
        }
        if (admissionsLeadSourceIds) {
            filter.admissionsLeadSourceIds = {
                operator: admissionsLeadSourceIds.operator,
                value: admissionsLeadSourceIds.value.map(({id}) => id)
            }
        }
        if (leadTypeIds) {
            filter.leadTypeIds = {
                operator: leadTypeIds.operator,
                value: leadTypeIds.value.map(({leadTypeId}) => leadTypeId)
            }
        }
        if (isAccessDisabled) {
            filter.isAccessDisabled = {
                ...isAccessDisabled,
                value: !!isAccessDisabled?.value?.id
            }
        }
        if (phases) {
            filter.phases = {
                ...phases,
                value: phases.value.map(({phaseId}) => phaseId)
            }
        }
        if (shifts) {
            filter.shifts = {
                ...shifts,
                value: shifts.value.map(({shiftId}) => shiftId)
            }
        }

        if (admAdvisors) {
            filter.admAdvisorProfileIds = {
                operator: admAdvisors.operator,
                value: admAdvisors.value.map((profile) => profile.profileId)
            }
        }
        if (faAdvisors) {
            filter.faAdvisorProfileIds = {
                operator: faAdvisors.operator,
                value: faAdvisors.value.map((profile) => profile.profileId)
            }
        }
        if (acadAdvisors) {
            filter.acadAdvisorProfileIds = {
                operator: acadAdvisors.operator,
                value: acadAdvisors.value.map((profile) => profile.profileId)
            }
        }

        if (status) {
            filter.statusIds = {
                operator: status.operator,
                value: status.value.map(({statusId}) => statusId)
            }
        }
        if (state) {
            filter.states = {
                operator: state.operator,
                value: state.value.map(({id}) => id)
            }
        }
        if (profileStatus) {
            filter.profileStatuses = {
                operator: profileStatus.operator,
                value: profileStatus.value.map(({id}) => id === "active")
            }
        }
        if (term) {
            filter.termIds = {
                operator: term.operator,
                value: term.value.map(({id}) => id)
            }
        }
        if (startingTerm) {
            filter.startingTermIds = {
                operator: startingTerm.operator,
                value: startingTerm.value.map(({id}) => id)
            }
        }
        if (currentTerm) {
            filter.currentTermIds = {
                operator: currentTerm.operator,
                value: currentTerm.value.map(({id}) => id)
            }
        }
        if (activityIds) {
            filter.activityIds = {
                operator: activityIds.operator,
                value: activityIds.value.map(({activityId}) => activityId)
            }
        }
        if (activityDescription) {
            filter.activityDescription = activityDescription
        }
        if (applicationCompletedDate) {
            filter.applicationCompletedDate = {
                operator: applicationCompletedDate.operator,
                value: formatDateFilterValue(applicationCompletedDate.operator, applicationCompletedDate.value)
            }
        }
        if (startDateRange) {
            filter.startDateRange = {
                operator: startDateRange.operator,
                value: formatDateFilterValue(startDateRange.operator, startDateRange.value)
            }
        }
        if (createdAtRange) {
            filter.createdAtRange = {
                operator: createdAtRange.operator,
                value: formatDateFilterValue(createdAtRange.operator, createdAtRange.value)
            }
        }
        if (inquiryDateRange) {
            filter.inquiryDateRange = {
                operator: inquiryDateRange.operator,
                value: formatDateFilterValue(inquiryDateRange.operator, inquiryDateRange.value)
            }
        }
        if (lastActivityDateRange) {
            filter.lastActivityDateRange = {
                operator: lastActivityDateRange.operator,
                value: formatDateFilterValue(lastActivityDateRange.operator, lastActivityDateRange.value)
            }
        }
        if (sapCheckpoints) {
            filter.sapCheckpoints = {
                operator: sapCheckpoints.operator,
                value: sapCheckpoints.value.map(({id}) => id)
            }
        }
        if (retentionLevels) {
            filter.retentionLevels = {
                operator: retentionLevels.operator,
                value: retentionLevels.value.map(({id}) => id)
            }
        }

        if (temporaryOutDateRange) {
            filter.temporaryOutDateRange = {
                operator: temporaryOutDateRange.operator,
                value: formatDateFilterValue(temporaryOutDateRange.operator, temporaryOutDateRange.value)
            }
        }
        if (withdrawalDateRange) {
            filter.withdrawalDateRange = {
                operator: withdrawalDateRange.operator,
                value: formatDateFilterValue(withdrawalDateRange.operator, withdrawalDateRange.value)
            }
        }

        if (users) {
            filter.userIds = {
                operator: users.operator,
                value: users.value.map(({id}) => id)
            }
        }

        if (countries) {
            filter.countryIds = {
                operator: countries.operator,
                value: countries.value.map(({name}) => name)
            }
        }

        if (attendancePercentage) {
            filter.attendancePercentage = attendancePercentage
        }

        return filter
    }, [filters])
